/**
 * Temu 发品包装 / 敏感属性 / SKU 多包规（对齐 bg.goods.add）。
 * 站内录入单位固定为公制：重量 g、体积 cm；提交时换算为 mg / mm。
 */

export const TEMU_WEIGHT_UNIT = 'g';
export const TEMU_DIM_UNIT = 'cm';

export const TEMU_SKU_CLASSIFICATION_OPTIONS = [
  { value: '1', label: '单品' },
  { value: '2', label: '同款多件装' },
  { value: '3', label: '混合套装' },
];

/** pieceUnitCode：1 件 / 2 双 / 3 包 */
export const TEMU_PIECE_UNIT_OPTIONS = [
  { value: '1', label: '件' },
  { value: '2', label: '双' },
  { value: '3', label: '包' },
];

export const TEMU_SENSITIVE_YES_NO_OPTIONS = [
  { value: '0', label: '否' },
  { value: '1', label: '是' },
];

/** sensitiveList 码（与文档一致） */
export const TEMU_SENSITIVE_LIST_OPTIONS = [
  { value: '110001', label: '纯电' },
  { value: '120001', label: '内电' },
  { value: '130001', label: '磁性' },
  { value: '140001', label: '液体' },
  { value: '150001', label: '粉末' },
  { value: '160001', label: '膏体' },
  { value: '170001', label: '刀具' },
];

export const TEMU_INDIVIDUALLY_PACKED_OPTIONS = [
  { value: '1', label: '独立包装' },
  { value: '0', label: '非独立包装' },
];

/** 净含量单位码（productSkuNetContentReq.netContentUnitCode） */
export const TEMU_NET_CONTENT_UNIT_OPTIONS = [
  { value: '1', label: '液体盎司' },
  { value: '2', label: '毫升' },
  { value: '3', label: '加仑' },
  { value: '4', label: '升' },
  { value: '5', label: '克' },
  { value: '6', label: '千克' },
  { value: '7', label: '常衡盎司' },
  { value: '8', label: '磅' },
];

export function normalizeSensitiveList(list) {
  const out = [];
  const seen = new Set();
  (Array.isArray(list) ? list : []).forEach((x) => {
    const n = Number(x);
    if (!Number.isFinite(n) || n <= 0 || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  });
  return out;
}

/** UI 上最多 3 个敏感类型下拉，与卖家中心一致 */
export function sensitiveSlotsFromList(list, slotCount = 3) {
  const codes = normalizeSensitiveList(list).map(String);
  const slots = [];
  for (let i = 0; i < slotCount; i += 1) {
    slots.push(codes[i] || '');
  }
  return slots;
}

export function sensitiveListFromSlots(slots) {
  return normalizeSensitiveList(slots);
}

/**
 * 组装 productSkuMultiPackReq（单品也传，平台可识别）。
 */
export function buildProductSkuMultiPackReq(sku = {}) {
  const classification = Number(sku.skuClassification || 1);
  const pieceUnitCode = Number(sku.pieceUnitCode || sku.quantityUnit || 1);
  const piecesRaw = sku.numberOfPieces ?? sku.quantity;
  let numberOfPieces = Number(piecesRaw);
  if (!Number.isFinite(numberOfPieces) || numberOfPieces <= 0) {
    numberOfPieces = classification === 1 ? 1 : 0;
  }
  const totalContains = Number(sku.totalContains);
  if (classification !== 1 && Number.isFinite(totalContains) && totalContains > 0) {
    numberOfPieces = totalContains;
  }
  const req = {
    skuClassification: [1, 2, 3].includes(classification) ? classification : 1,
    numberOfPieces: Math.max(1, Math.trunc(numberOfPieces || 1)),
    pieceUnitCode: [1, 2, 3].includes(pieceUnitCode) ? pieceUnitCode : 1,
    // GLO 样例：多包规内带空净含量对象；有净含量时用整数单位码（5=克）
    productSkuNetContentReq: buildProductSkuNetContentReq(sku) || {},
  };
  if (req.skuClassification === 2 || req.skuClassification === 3) {
    const packed = Number(sku.individuallyPacked);
    if (packed === 0 || packed === 1) {
      req.individuallyPacked = packed;
    }
  }
  return req;
}

/**
 * 计量净含量（MultiPack.productSkuNetContentReq）。
 * netContentUnitCode：1液体盎司 2毫升 3加仑 4升 5克 6千克 7常衡盎司 8磅
 * 严禁用 sku.netWeight / 货品净重 productSkuNetWeightReq 顶替。
 */
export function buildProductSkuNetContentReq(sku = {}) {
  // 只读净含量字段；故意不读 netWeight
  const raw = String(sku.netContentNumber ?? sku.netContent ?? '').trim();
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unitCode = Number(sku.netContentUnitCode);
  return {
    netContentUnitCode: [1, 2, 3, 4, 5, 6, 7, 8].includes(unitCode) ? unitCode : 5,
    netContentNumber: Math.trunc(n),
  };
}

export function buildProductSkuSensitiveAttrReq(sku = {}, formFallback = {}) {
  const isSensitive = Number(
    sku.isSensitive != null ? sku.isSensitive : (formFallback.isSensitive ?? 0),
  ) === 1 ? 1 : 0;
  const list = normalizeSensitiveList(
    sku.sensitiveList != null ? sku.sensitiveList : formFallback.sensitiveList,
  );
  // 官方样例必传：非敏感也要 { isSensitive: 0, sensitiveList: [] }
  return {
    isSensitive,
    sensitiveList: isSensitive ? list : [],
  };
}

/** 建议零售价（可选；有值才传）。suggestedPrice 为站内小数，提交转最小货币单位。 */
export function buildProductSkuSuggestedPriceReq(sku = {}, fallbackCurrency = 'USD') {
  const raw = String(sku.suggestedPrice ?? '').trim();
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  const currency = String(
    sku.suggestedPriceCurrencyType || fallbackCurrency || 'USD',
  ).trim().toUpperCase() || 'USD';
  return {
    suggestedPriceCurrencyType: currency,
    suggestedPrice: Math.round(n * 100),
  };
}

/**
 * 包装清单（混合套装必填）。
 * @returns {null|{ productSkuAccessories: object[] }}
 */
export function buildProductSkuAccessoriesReq(sku = {}) {
  const list = (Array.isArray(sku.accessories) ? sku.accessories : [])
    .map((row) => {
      const vid = Number(row?.vid);
      const num = Number(row?.num);
      const unitCode = Number(row?.unitCode || 1);
      if (!(vid > 0) || !(num > 0)) return null;
      return {
        vid: Math.trunc(vid),
        num: Math.trunc(num),
        unitCode: [1, 2, 3].includes(unitCode) ? unitCode : 1,
        thumbUrl: String(row?.thumbUrl || '').trim(),
      };
    })
    .filter(Boolean);
  if (!list.length) return null;
  return { productSkuAccessories: list.slice(0, 20) };
}

export function accessoriesQuantitySum(sku = {}) {
  return (Array.isArray(sku.accessories) ? sku.accessories : [])
    .reduce((sum, row) => sum + (Number(row?.num) > 0 ? Math.trunc(Number(row.num)) : 0), 0);
}
